/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import type { AnalyticsAttributes, Attributes } from '@atlassian/jira-product-analytics-bridge';
import type { EventId, Source } from '@jira/forge__forge-ui-types/src/common/types/analytics.tsx';
import { getAnalyticsWebClientPromise } from '@jira/platform__observability-product-analytics-web-client-async';
import { PACKAGE_NAME_GASV3 } from '../../../constants.tsx';
import { createAttributes, parseSource } from '../create-attributes/index.tsx';
import { ANALYTICS_UTIL, logError } from '../logger/logError.tsx';
import { isSynthetic } from '../synthetic/isSynthetic.tsx';

type EventProps = {
	id: EventId;
	action: string;
	source?: Source;
	attributes?: AnalyticsAttributes;
};

export type EventInputProps = Flow.Diff<
	EventProps,
	{
		action: string;
	}
>;

const createAnalyticsProps = ({
	id,
	// Since source is required in sendOperationalEvent and sendUIEvent we need to pass here a fallback
	// in case source is not provided
	source = 'unknownSource',
	action,
	attributes,
}: EventProps) => ({
	actionSubject: `${PACKAGE_NAME_GASV3}.${id}`,
	attributes: createAttributes(attributes),
	source,
	action,
});

const getAnalyticsClient = async () => (await getAnalyticsWebClientPromise()).getInstance();

const sendOperationalEvent = async (props: EventProps) =>
	(await getAnalyticsClient()).sendOperationalEvent(createAnalyticsProps(props));

const sendUiEvent = async (props: EventProps) =>
	(await getAnalyticsClient()).sendUIEvent(createAnalyticsProps(props));

const sendTrackEvent = async (props: EventProps) =>
	(await getAnalyticsClient()).sendTrackEvent(createAnalyticsProps(props));

export const fireOperationalEvent = async (
	action: string,
	{ source, ...eventProps }: EventInputProps,
): Promise<void> =>
	sendOperationalEvent({
		...eventProps,
		source: parseSource(source),
		action,
	});

export const fireUiEvent = async (
	action: string,
	{ source, ...eventProps }: EventInputProps,
): Promise<void> =>
	sendUiEvent({
		...eventProps,
		source: parseSource(source),
		action,
	});

export const fireTrackEvent = async (
	action: string,
	{ source, ...eventProps }: EventInputProps,
): Promise<void> =>
	sendTrackEvent({
		...eventProps,
		source: parseSource(source),
		action,
	});

export type InitializationProps = {
	id: EventId;
	source?: Source;
	attributes?: Attributes;
};

export const fireInitializationEvent = async (
	action: string,
	{ id, attributes, source }: InitializationProps,
	// oxlint-disable-next-line typescript/no-invalid-void-type, @typescript-eslint/no-explicit-any
): Promise<[void, any]> =>
	Promise.all([
		fireOperationalEvent(action, {
			id,
			attributes,
			source,
		}),
		(attributes?.error &&
			logError(
				ANALYTICS_UTIL,
				`Something went wrong with Forge initialization, isSynthetic=${isSynthetic().toString()}, id=${id}, source=${parseSource(
					source,
				)}, action=${action}`,
				attributes.error,
			)) ||
			Promise.resolve(),
	]);
